import { format, formatDistanceToNow, isValid } from 'date-fns'
import { Check, Copy, DatabaseZap, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import {
  CONNECTION_STATUS,
  ConfigAnalyticsApiError,
  Connection,
  configAnalyticsClient,
  configAnalyticsUrl
} from '@/api/config-analytics-client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { toast } from '@/components/ui/use-toast'

const principles = [
  {
    title: 'Standard MCP connection',
    description:
      'Add the server to your MCP client. It opens this portal, you approve the connection, and the client gets its own short-lived token. Nothing to copy.'
  },
  {
    title: 'Audited',
    description:
      'Every query your agent runs is logged with the connection and your user.'
  },
  {
    title: 'Time-limited',
    description:
      'A connection lasts up to 90 days. Revoke it here as soon as you no longer need it.'
  },
  {
    title: 'Configuration only',
    description:
      'Journeys, automations, workflows, schemas, products and more across customer orgs. No business records, no personal data, read-only.'
  }
]

const formatTime = (iso?: string) => {
  if (!iso) return '—'

  const date = new Date(iso)

  return isValid(date) ? format(date, 'dd MMM yyyy, HH:mm') : iso
}

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : String(error)

const CopyBlock = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
  }

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-primary">{label}</p>
      <div className="flex items-start gap-2">
        <code className="block flex-1 rounded bg-background p-2 text-xs text-primary break-all">
          {value}
        </code>
        <Button onClick={() => void copy()} size="sm" variant="outline">
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </Button>
      </div>
    </div>
  )
}

const formatExpiry = (connection: Connection) => {
  const date = new Date(connection.expires_at)

  if (!isValid(date)) return connection.expires_at
  if (connection.status !== CONNECTION_STATUS.ACTIVE)
    return formatTime(connection.expires_at)

  return `${formatTime(connection.expires_at)} (in ${formatDistanceToNow(
    date
  )})`
}

const ConnectionsTable = ({
  connections,
  busyId,
  onRevoke,
  showOwner
}: {
  connections: Connection[]
  busyId: string | null
  onRevoke: (id: string) => void
  showOwner?: boolean
}) => (
  <Table className="text-primary">
    <TableHeader>
      <TableRow>
        <TableHead>Client</TableHead>
        {showOwner && <TableHead>Owner</TableHead>}
        <TableHead>Connected</TableHead>
        <TableHead>Expires</TableHead>
        <TableHead>Status</TableHead>
        <TableHead />
      </TableRow>
    </TableHeader>
    <TableBody>
      {connections.map((connection) => (
        <TableRow key={connection.id}>
          <TableCell>{connection.client_name}</TableCell>
          {showOwner && (
            <TableCell>{connection.email ?? connection.user_id}</TableCell>
          )}
          <TableCell>{formatTime(connection.created_at)}</TableCell>
          <TableCell>{formatExpiry(connection)}</TableCell>
          <TableCell>
            <Badge
              variant={
                connection.status === CONNECTION_STATUS.ACTIVE
                  ? 'default'
                  : 'outline'
              }
            >
              {connection.status}
            </Badge>
          </TableCell>
          <TableCell className="text-right">
            {connection.status === CONNECTION_STATUS.ACTIVE && (
              <Button
                disabled={busyId === connection.id}
                onClick={() => onRevoke(connection.id)}
                size="sm"
                variant="outline"
              >
                Revoke
              </Button>
            )}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
)

export default function ConfigAnalyticsAccessPage() {
  const [mine, setMine] = useState<Connection[]>([])
  const [all, setAll] = useState<Connection[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const [own, everyone] = await Promise.all([
        configAnalyticsClient.listConnections('mine'),
        configAnalyticsClient.listConnections('all').catch((error) => {
          if (error instanceof ConfigAnalyticsApiError && error.status === 403)
            return null
          throw error
        })
      ])

      setMine(own)
      setAll(everyone)
    } catch (error) {
      toast({
        title: 'Could not load connections',
        description: errorMessage(error),
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const revoke = async (id: string) => {
    setBusyId(id)
    try {
      await configAnalyticsClient.revoke(id)
      toast({ title: 'Connection revoked' })
      await refresh()
    } catch (error) {
      toast({
        title: 'Could not revoke',
        description: errorMessage(error),
        variant: 'destructive'
      })
    } finally {
      setBusyId(null)
    }
  }

  const mineIds = new Set(mine.map((c) => c.id))
  const others = (all ?? []).filter((c) => !mineIds.has(c.id))
  const mcpUrl = `${configAnalyticsUrl()}/mcp`

  return (
    <div className="space-y-6 p-6 max-w-4xl">
      <div className="space-y-2">
        <h1 className="text-xl font-semibold text-primary flex items-center gap-2">
          <DatabaseZap className="text-primary" size={20} />
          Config analytics access
        </h1>
        <p className="text-sm text-muted-foreground">
          The config analytics MCP server lets AI agents answer questions across
          the configuration of all ingested customer orgs with SQL.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {principles.map((item) => (
          <div className="rounded-md border border-border p-4" key={item.title}>
            <p className="font-semibold text-primary text-sm">{item.title}</p>
            <p className="text-sm text-muted-foreground mt-1 leading-snug">
              {item.description}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-md bg-accent p-4 space-y-3">
        <CopyBlock
          label="Connect Claude Code (it opens this portal to approve the connection)"
          value={`claude mcp add --transport http config-analytics ${mcpUrl}`}
        />
        <CopyBlock label="MCP server URL (other clients)" value={mcpUrl} />
      </div>

      <section className="space-y-2">
        <h2 className="text-base font-semibold text-primary">
          Your connections
        </h2>
        {loading && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="animate-spin" size={14} /> Loading…
          </p>
        )}
        {!loading && mine.length === 0 && (
          <p className="text-sm text-muted-foreground">No connections yet.</p>
        )}
        {mine.length > 0 && (
          <ConnectionsTable
            busyId={busyId}
            connections={mine}
            onRevoke={(id) => void revoke(id)}
          />
        )}
      </section>

      {all && others.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-base font-semibold text-primary">
            All connections (admins)
          </h2>
          <ConnectionsTable
            busyId={busyId}
            connections={others}
            onRevoke={(id) => void revoke(id)}
            showOwner
          />
        </section>
      )}
    </div>
  )
}
